import { useCookies } from '@/app/cookie-provider';
import BadgeStoreFront from '@/components/badge-storefront';
import DynamicImage from '@/components/commons/dynamic-image';
import PriceBookIcon from '@/components/icons/price-book-icon';
import ModalAlert from '@/components/sections/modal-alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import {
  useMutateApplyPromotion,
  useMutateValidatePromotion,
} from '@/hooks/api/storefront/promotion';
import useModal from '@/hooks/useModal';
import { deleteCartItem, updateCartItem } from '@/lib/actions/cart/action';
import { updateCartCheckout } from '@/lib/actions/checkout/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { queryClient } from '@/lib/query-client';
import {
  calculateTotalDiscountPerPriority,
  cn,
  formatIDR,
  formatNumber,
  generatePromotionsPayload,
  hexToRgba,
  isEmpty,
  parseErrorMessage,
  truncateText,
} from '@/lib/utils';
import { ThemeColorSchemeType, ThemeType } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { HttpTypes } from '@/types';
import { StoreCart } from '@/types/http';
import { BaseCart } from '@/types/http/cart/common';
import { BasePromotion } from '@/types/http/promotion/common';
import { AxiosError } from 'axios';
import {
  ChevronRight,
  CircleAlert,
  Gift,
  Loader2,
  MinusIcon,
  PlusIcon,
  TicketPercent,
  Trash2,
  XIcon,
} from 'lucide-react';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

type CartListProps = {
  cart: HttpTypes.StoreCart;
  cartItems: BaseCart[];
  host: string;
  token?: string;
  isPreview?: boolean;
  theme: ThemeType;
  themeId?: string;
  currentItemQty: { [key: string]: number };
  subtotal?: number;
  setCurrentItemQty: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>;
  setOpenModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerView?: React.Dispatch<
    React.SetStateAction<'cart' | 'voucher' | 'gift' | 'active_offers'>
  >;
  recommendPromotion?: BasePromotion[];
  isLoadingRecommend?: boolean;
  payloadListPromotion: {
    items: {
      item_id: string;
      qty: number;
      amount: number;
    }[];
    grand_total: number;
    current_date: string;
  };
  listPromotions?: BasePromotion[];
  setPromoReward: React.Dispatch<React.SetStateAction<BasePromotion | null>>;
  setMaxQtyReward: React.Dispatch<React.SetStateAction<number>>;
  colorScheme?: ThemeColorSchemeType;
};

export default function CartList({
  cart,
  cartItems,
  host,
  token,
  isPreview,
  theme,
  themeId,
  currentItemQty,
  subtotal,
  setCurrentItemQty,
  setOpenModal,
  setDrawerView,
  recommendPromotion,
  isLoadingRecommend,
  payloadListPromotion,
  setPromoReward,
  setMaxQtyReward,
  colorScheme,
}: CartListProps) {
  const { multiOrigin } = useCookies();
  const router = useRouter();
  const pathname = usePathname();
  const modalConfirmSpecific = useModal<{
    onConfirm: () => void;
    onCancel?: () => void;
  }>();

  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});

  const promotionStore = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      selectedFreeGift: state.selectedFreeGift,
      removeFreeGift: state.removeFreeGift,
      replacePromotion: state.replacePromotion,
      removePromotion: state.removePromotion,
      removeFreeGiftByItemId: state.removeFreeGiftByItemId,
    }),
    shallow
  );

  const hasSelectedPromos = promotionStore.selectedPromotion.length > 0;
  const selectedPromosName = promotionStore.selectedPromotion
    .map((p) => p.promotion_name)
    .join(', ');

  const { mutateAsync: mutateApplyPromotion } = useMutateApplyPromotion({
    onError: async (error) => {
      if (error instanceof AxiosError) {
        if (error.status === 401) {
          await fetch('/api/remove-cart-cookie', { method: 'GET' });

          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Something went wrong',
            variant: 'destructive',
          });

          return (window.location.href = '/');
        }
      }
    },
  });

  const validatePromotion = useMutateValidatePromotion({
    onSuccess: (data) => {
      const isValid = data.data.is_valid;
      const promoRewards = data.data.promotion_rewards;
      if (isValid && promoRewards) {
        promoRewards.forEach((promo: BasePromotion) => {
          promotionStore.replacePromotion(promo);
        });
      }
    },
  });

  const getUpdatedItems = (quantities: typeof currentItemQty, items: typeof cartItems) => {
    return items.map((item) => ({
      ...item,
      quantity: quantities[item.id] ?? item.quantity,
    }));
  };

  const tryValidatePromotion = async (customItems: typeof cartItems) => {
    const promotions = promotionStore.selectedPromotion
      .filter((promo) => promo.product_rules_type === 'specific' || promo.use_voucher)
      .map((promo) => ({
        promotion_id: promo.promotion_id,
        voucher_code: promo.voucher_code ?? '',
      }));

    if (promotions.length === 0) return;

    const items = customItems.map((item) => {
      return {
        item_id: item.id,
        qty: item.quantity,
        amount: Number(item.sell_price),
      };
    });

    const payload = {
      items,
      promotions,
      grand_total: items.reduce((sum, item) => sum + item.amount * item.qty, 0),
    };

    await validatePromotion.mutateAsync({
      body: payload,
      token: token as string,
    });
  };

  const debounceTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const processQtyChange = async (item: StoreCart, qty: number) => {
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const updatedQuantities = {
      ...currentItemQty,
      [item.id]: qty,
    };

    const updatedItems = getUpdatedItems(updatedQuantities, cartItems);

    try {
      await tryValidatePromotion(updatedItems);
      setCurrentItemQty(updatedQuantities);
      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = JSON.parse(error.response?.data.message);
        if (message?.promotion_id) {
          modalConfirmSpecific.open({
            onConfirm: async () => {
              promotionStore.removePromotion(message.promotion_id);
              modalConfirmSpecific.close();
              setCurrentItemQty(updatedQuantities);
              await updateCartItem(host as string, { ...item, quantity: qty });
              queryClient.invalidateQueries({ queryKey: ['carts'] });
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
            },
            onCancel: () => {
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
          });
        } else {
          setIsLoading((prev) => ({ ...prev, [item.id]: false }));
        }
      } else {
        setIsLoading((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  const debouncedQtyChange = (item: StoreCart, qty: number) => {
    setCurrentItemQty((prev) => ({
      ...prev,
      [item.id]: qty,
    }));

    if (debounceTimers.current[item.id]) {
      clearTimeout(debounceTimers.current[item.id]);
    }

    debounceTimers.current[item.id] = setTimeout(() => {
      processQtyChange(item, qty);
    }, 1000);
  };

  const handleIncrementQty = (item: StoreCart, idx: number = 0) => {
    const newQty = (currentItemQty[item.id] || item.quantity) + 1;
    if (newQty > (cart?.item_stock?.[idx]?.stock || 0)) return;
    debouncedQtyChange(item, newQty);
  };

  const handleDecrementQty = (item: StoreCart) => {
    const newQty = Math.max(1, (currentItemQty[item.id] || item.quantity) - 1);
    debouncedQtyChange(item, newQty);
  };

  const removeItem = async (item: StoreCart) => {
    if (isPreview) return;
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const updatedItems = getUpdatedItems(
      currentItemQty,
      cartItems.filter((i) => i.id !== item.id)
    );

    try {
      await tryValidatePromotion(updatedItems);
      await deleteCartItem(host as string, item.id);
      queryClient.invalidateQueries({ queryKey: ['carts'] });

      // Send analytics events
      SendEventAnalytic('remove_from_cart', {
        currency: 'IDR',
        value: Number(item.sell_price) * item.quantity,
        items: [
          {
            item_name: item?.product_name,
            item_id: item.id as string,
            price: Number(item?.sell_price),
            quantity: item.quantity,
            unique_action_id: Date.now(),
          },
        ],
      });
      SendEventMetapixel('RemoveFromCart', {
        content_ids: [item.id],
        content_name: item.product_name,
        content_type: 'product',
        quantity: item.quantity,
        value: Number(item.sell_price) * item.quantity,
        currency: 'IDR',
      });

      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = JSON.parse(error.response?.data.message);
        if (message?.promotion_id) {
          modalConfirmSpecific.open({
            onConfirm: async () => {
              promotionStore.removePromotion(message.promotion_id);
              modalConfirmSpecific.close();

              await deleteCartItem(host as string, item.id);
              queryClient.invalidateQueries({ queryKey: ['carts'] });

              // Send analytics events
              SendEventAnalytic('remove_from_cart', {
                currency: 'IDR',
                value: Number(item.sell_price) * item.quantity,
                items: [
                  {
                    item_name: item?.product_name,
                    item_id: item.id as string,
                    price: Number(item?.sell_price),
                    quantity: item.quantity,
                    unique_action_id: Date.now(),
                  },
                ],
              });
              SendEventMetapixel('RemoveFromCart', {
                content_ids: [item.id],
                content_name: item.product_name,
                content_type: 'product',
                quantity: item.quantity,
                value: Number(item.sell_price) * item.quantity,
                currency: 'IDR',
              });

              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
            },
            onCancel: () => {
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
          });
        } else {
          setIsLoading((prev) => ({ ...prev, [item.id]: false }));
        }
      } else {
        setIsLoading((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  const handleToCart = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, ['checkout']: true }));

      if (isPreview) return;

      if (promotionStore.selectedPromotion.length > 0) {
        const promotions_payload = {
          cart_id: cart.cartId as string,
          promotions: generatePromotionsPayload(
            promotionStore.selectedPromotion,
            promotionStore.selectedFreeGift
          ),
        };

        await mutateApplyPromotion({
          body: promotions_payload ?? { cart_id: cart.id, promotions: [] },
          token,
        });
      }

      const result = await updateCartCheckout(host as string);

      if (result.success) {
        return router.push(`/checkout/${result.data.checkout_id}`);
      }

      if (!cartItems?.length) {
        return toast({
          title: 'Error',
          description: 'Cart is empty',
          variant: 'destructive',
        });
      }

      if (result.status === 404) {
        await fetch('/api/remove-cart-cookie', { method: 'GET' });

        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });

        return (window.location.href = '/');
      }

      if (result.status === 400) {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data.data || error.response?.data?.message;
        toast({
          title: 'Error',
          description: parseErrorMessage(errorMessage) || 'Something went wrong',
          variant: 'destructive',
        });

        if (error.status === 401) {
          await fetch('/api/remove-cart-cookie', { method: 'GET' });

          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Something went wrong',
            variant: 'destructive',
          });

          return (window.location.href = '/');
        }
      }
    } finally {
      setOpenModal?.(false);
      setIsLoading((prev) => ({ ...prev, ['checkout']: false }));
    }
  };

  const handleOutOfStock = (idx: number) => {
    return cart?.item_stock?.[idx]?.stock === 0;
  };

  const checkStockAfterChangeOrigin = (idx: number, quantity: number): boolean => {
    const stockValue = cart?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity > stock;
  };
  const handleDisableAction = (idx: number, quantity: number): boolean => {
    const stockValue = cart?.item_stock?.[idx]?.stock;
    const stock = Number(stockValue) || 0;
    return quantity >= stock;
  };

  const disableButton = cart?.item_stock?.some((item) => item.stock === 0);
  const discount = calculateTotalDiscountPerPriority(
    promotionStore.selectedPromotion,
    subtotal as number
  );

  return (
    <>
      <DrawerHeader className='flex items-center justify-between text-left'>
        <DrawerTitle className='theme_cart_drawer_color'>Your Cart</DrawerTitle>
        <DrawerClose>
          <XIcon className='h-4 w-4' />
        </DrawerClose>
      </DrawerHeader>
      <div className='px-4'>
        <div className='theme_cart_drawer_color grid grid-cols-6 border-b py-2 text-xs uppercase'>
          <div className='col-span-4'>Product</div>
          <div className='col-span-2 text-right'>Total</div>
        </div>
      </div>
      <ScrollArea
        className='h-full overflow-y-auto px-4'
        type='scroll'
        style={
          {
            '--background': hexToRgba(colorScheme?.background, 0.1) || '#F3F4F6',
            '--text-primary': colorScheme?.text || '#111827',
            '--text-secondary': colorScheme?.textSecondary || '#6B7280',
          } as React.CSSProperties
        }
      >
        {cartItems?.map((item, idx) => {
          const images = (item.product_assets || []).filter(
            (asset) => asset.assets_type === 'image'
          );
          const displayedQuantity = currentItemQty[item.id] || item.quantity;
          const totalPrice =
            displayedQuantity *
            (item.discount_price && item.pricebook_id === null
              ? item.discount_price
              : item.sell_price);
          const isOutOfStock = handleOutOfStock(idx || 0);
          const disable = handleDisableAction(idx || 0, item.quantity);
          const showNotifyStock = checkStockAfterChangeOrigin(idx || 0, item.quantity);
          const stock = Number(cart?.item_stock?.[idx]?.stock);
          const suggestionStock = (idx: number) => {
            return cart?.item_stock?.[idx]?.available_stock || [];
          };

          const suggestionPricebook = (idx: number) => {
            return cart?.items?.[idx]?.pricebook || [];
          };

          const isAnyPricebook = (cart?.items?.[idx]?.pricebook ?? []).length > 0;

          const isUsedPricebook = cart?.items?.[idx]?.pricebook_id !== null;

          return (
            <div className='theme_cart_drawer_color flex gap-x-2 border-b py-4' key={item.id}>
              <div className='mt-1 h-24 w-24'>
                <Image
                  src={images[0]?.assets_url}
                  alt={item.product_name}
                  width={80}
                  height={80}
                  className={cn(
                    'aspect-square w-36 border object-contain',
                    isOutOfStock && 'grayscale'
                  )}
                />
              </div>
              <div className='flex flex-1 flex-col gap-y-2'>
                <div className={cn('w-fit', isOutOfStock && 'grayscale')}>
                  <BadgeStoreFront theme={theme} item={item} />
                </div>

                <div className='grid grid-cols-6'>
                  <div className='col-span-4'>
                    <div className='flex gap-x-3'>
                      <div className='w-full space-y-1'>
                        <h2 className={cn('line-clamp-3 text-sm', isOutOfStock && 'text-gray-500')}>
                          {item.product_name}
                        </h2>
                        {item.variants?.map((variant) => (
                          <p
                            key={variant.option_id}
                            className={cn('text-gray-500', isOutOfStock && 'text-gray-500')}
                          >
                            {variant.label}
                          </p>
                        ))}
                        <div
                          className={cn(item?.discount_price && 'flex flex-col items-start gap-2')}
                        >
                          <span
                            className={cn(
                              ' text-base font-bold',
                              isOutOfStock ? '!text-gray-500' : 'text_product_secondary'
                            )}
                          >
                            {formatIDR(
                              item?.discount_price && item.pricebook_id === null
                                ? item?.discount_price
                                : item?.sell_price || 0
                            )}
                          </span>

                          {(item?.discount_price as number) > 0 && !isUsedPricebook ? (
                            <span
                              className={cn(
                                'text-sm text-gray-500 line-through',
                                isOutOfStock && 'text-gray-500'
                              )}
                            >
                              {formatIDR(item?.sell_price || 0)}
                            </span>
                          ) : null}
                        </div>
                        <div className='flex gap-x-2 pt-2'>
                          <div className='flex h-8 w-fit items-center rounded border'>
                            <button
                              onClick={() => handleDecrementQty(item)}
                              className='h-full px-3 '
                            >
                              <MinusIcon className='h-3 w-3 cursor-pointer' />
                            </button>
                            <span className='mx-2'>{formatNumber(displayedQuantity)}</span>
                            <button
                              onClick={() => handleIncrementQty(item, idx)}
                              className={cn('h-full px-3', disable && 'cursor-not-allowed')}
                              disabled={disable}
                            >
                              <PlusIcon className='h-3 w-3 cursor-pointer' />
                            </button>
                          </div>
                          <button onClick={() => removeItem(item)} disabled={isLoading[item.id]}>
                            <Trash2 className='h-3 w-3' />
                          </button>
                          {multiOrigin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                                <CircleAlert className='h-3 w-3' />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='center' className='z-[1000] w-[200px]'>
                                <DropdownMenuLabel>See Where It’s Available</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                  {suggestionStock(idx).map((stockItem, index) => (
                                    <DropdownMenuItem
                                      key={index}
                                      className='flex justify-between gap-x-3'
                                    >
                                      <span>{stockItem.location_name}</span>
                                      <span>{stockItem.quantity}</span>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {isAnyPricebook && isUsedPricebook && (
                            <DropdownMenu>
                              <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                                <PriceBookIcon
                                  className='h-4 w-4'
                                  color={theme.cart?.drawerColorScheme?.text}
                                />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='center' className='z-[1000] w-[250px] '>
                                <DropdownMenuLabel className='text-sm'>
                                  Tiered Pricing
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                  {suggestionPricebook(idx).map((item, index) => {
                                    const label = `Buy ${
                                      item.min_unit === 1 ? '1' : `> ${(item?.min_unit ?? 0) - 1}`
                                    } for ${formatIDR(item.retail_price ?? 0)}/unit`;
                                    return (
                                      <DropdownMenuItem
                                        key={index}
                                        className='flex justify-between gap-x-3 text-xs'
                                      >
                                        <span>{label}</span>
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      ' col-span-2 text-right text-sm',
                      isOutOfStock ? 'text-gray-500' : 'theme_cart_drawer_color'
                    )}
                  >
                    {formatIDR(totalPrice)}
                  </div>
                </div>
                {multiOrigin && (
                  <div className='w-full'>
                    {multiOrigin &&
                      ((stock === 0 && isOutOfStock) || (stock > 0 && showNotifyStock)) && (
                        <Badge variant='destructive' className='text-[10px]'>
                          {stock === 0 && isOutOfStock
                            ? 'Stock Not Available'
                            : 'Stock Is Not Enough'}
                        </Badge>
                      )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {promotionStore.selectedPromotion &&
          promotionStore.selectedPromotion.length > 0 &&
          promotionStore.selectedPromotion.map((promo) => {
            const rewards = promotionStore.selectedFreeGift?.filter(
              (item) => item.promotion_id === promo.promotion_id
            );

            return promo.product_reward && promo.product_reward.length > 0 ? (
              <div key={promo.promotion_id}>
                <Button
                  key={promo.promotion_id}
                  variant={null}
                  onClick={() => {
                    setPromoReward(promo);
                    setMaxQtyReward(
                      promo.product_reward && promo.product_reward[0]
                        ? promo.counted_multiply
                          ? promo.product_reward[0].qty * promo.counted_multiply
                          : promo.product_reward[0].qty
                        : 0
                    );
                    setDrawerView?.('gift');
                  }}
                  className={cn(
                    'w-full items-center justify-between rounded-none border-b px-4 py-6',
                    !isEmpty(colorScheme) && 'bg-[--background] text-[--text-primary]'
                  )}
                >
                  <span className='flex items-center gap-x-2'>{promo.promotion_name}</span>
                  <ChevronRight className='h-4 w-4' />
                </Button>
                {rewards.length > 0 &&
                  rewards.map((reward) =>
                    reward.items.map((item) => (
                      <div key={item.item_id} className='theme_cart_drawer_color flex gap-x-2 py-4'>
                        <div className='mt-1 h-24 w-24'>
                          <DynamicImage
                            src={
                              item.product_assets?.[0] ??
                              String(reward?.product_group.product_assets?.[0])
                            }
                            alt={item.item_name ?? String(reward?.product_group.product_name)}
                            width={80}
                            height={80}
                            className={cn('aspect-square w-36 border object-contain')}
                          />
                        </div>
                        <div className='flex flex-1 flex-col gap-y-2'>
                          <div className='w-fit'>
                            <div className='grid w-full gap-2'>
                              <Badge
                                variant='custom'
                                className='theme_badge_inside theme_badge_promotion w-full text-[12px] font-bold'
                              >
                                Free
                              </Badge>
                            </div>
                          </div>

                          <div className='grid grid-cols-6'>
                            <div className='col-span-4'>
                              <div className='flex gap-x-3'>
                                <div className='w-full space-y-1'>
                                  <h2 className='line-clamp-3 text-sm'>{item.product_name}</h2>
                                  <p className={cn('text-gray-500')}>{item.item_name}</p>
                                  <div className='flex flex-col items-start gap-2'>
                                    <span className={cn('text-sm text-gray-500 line-through')}>
                                      {formatIDR(Number(item?.sell_price))}
                                    </span>
                                  </div>
                                  <div className='flex gap-x-2 pt-2'>
                                    <div className='flex h-8 w-fit items-center rounded border'>
                                      <button className='h-full px-3' disabled>
                                        <MinusIcon className='h-3 w-3 cursor-pointer' />
                                      </button>
                                      <span className='mx-2'>
                                        {formatNumber(item.qty as number)}
                                      </span>
                                      <button className={cn('h-full px-3')} disabled>
                                        <PlusIcon className='h-3 w-3 cursor-pointer' />
                                      </button>
                                    </div>
                                    <button
                                      type='button'
                                      onClick={() => {
                                        promotionStore.removeFreeGiftByItemId(
                                          promo.promotion_id,
                                          item.item_id
                                        );
                                      }}
                                    >
                                      <Trash2 className='h-4 w-4' />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              className={cn(
                                ' theme_cart_drawer_color col-span-2 text-right text-sm'
                              )}
                            >
                              {formatIDR(0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
              </div>
            ) : null;
          })}
      </ScrollArea>
      <DrawerFooter
        className='border-t px-0 pt-0'
        style={
          {
            '--text-primary': colorScheme?.text || '#111827',
            '--text-secondary': colorScheme?.textSecondary || '#6B7280',
            '--background': hexToRgba(colorScheme?.background as string, 0.1) || '#F3F4F6',
            '--btn-background': colorScheme?.btnSolidBackgroud || '#F3F4F6',
            '--btn-label': colorScheme?.btnSolidlabel || '#111827',
          } as React.CSSProperties
        }
      >
        {/* Promotion */}
        <div className='flex flex-col gap-1'>
          {isLoadingRecommend ? (
            <div className='flex w-full flex-col gap-1.5'>
              <Skeleton className='h-20 w-full' />
            </div>
          ) : (
            <>
              <div className='relative flex h-full w-full'>
                {(recommendPromotion ?? [])?.map((promo, index) => {
                  const grandTotal = payloadListPromotion.grand_total;
                  const minSpending = promo.minimum_spending;
                  const spendMore = minSpending - (grandTotal || 0);

                  return (
                    <div className='border-0 px-1.5 py-1 shadow-sm' key={index}>
                      <Card>
                        <CardContent
                          className={cn(
                            'rounded-lg p-3',
                            !isEmpty(colorScheme)
                              ? 'bg-[--background] text-[--text-primary]'
                              : 'bg-yellow-100 text-black'
                          )}
                        >
                          <div className='flex items-center gap-4'>
                            <Gift className='h-6 w-6 flex-shrink-0' />
                            <p className='text-sm'>
                              Do you want receive more promotion? add{' '}
                              <span className='font-bold'>{formatIDR(spendMore)}</span> to your
                              cart!!!
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
              <div className='relative w-full'>
                <div className='flex w-full items-center bg-[--background] p-3 text-sm font-medium text-[--text-primary]'>
                  <button
                    type='button'
                    className='flex flex-1 items-center justify-start gap-x-2 rounded-none border-b'
                    onClick={() => setDrawerView?.('voucher')}
                    style={{
                      outline: 'none',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <TicketPercent className='size-6' />
                    {hasSelectedPromos ? truncateText(selectedPromosName, 35) : 'See all offers'}
                  </button>
                  <button
                    type='button'
                    onClick={() => setDrawerView?.('active_offers')}
                    className='mr-2 flex items-center rounded-none border-b'
                    style={{
                      outline: 'none',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                    aria-label='Show active offers'
                  >
                    <ChevronRight className='h-5 w-5' />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Subtotal */}
        <div className='theme_cart_drawer_color mb-4 px-4 pt-4'>
          <div className='mb-2 flex items-center justify-between text-base'>
            <span>Estimated total</span>
            <span>{formatIDR(Math.max(Number(subtotal) - discount.totalDiscount, 0))}</span>
          </div>
          {(discount.totalDiscount > 0 || isPreview) && (
            <div
              className='mb-2 flex items-center justify-between text-base text-[var(--color-text-secondary)]'
              style={
                {
                  '--color-text-secondary':
                    theme?.cart?.drawerColorScheme?.textSecondary || '#6B7280',
                } as React.CSSProperties
              }
            >
              <div>Saved</div>
              <div>{formatIDR(discount.totalDiscount || 0)}</div>
            </div>
          )}
          <p>Taxes, discounts and shipping calculated at checkout.</p>
        </div>
        <div className='flex w-full flex-col gap-3 px-4'>
          <Button
            type='button'
            variant='link'
            className='theme_button theme_cart_drawer_btn w-full items-center px-24 underline-offset-2'
            onClick={() => {
              setOpenModal?.(false);
              router.push(`/${token ? `?theme_id=${themeId}&token=${token}` : ''}`);
            }}
            disabled={isLoading['checkout']}
          >
            {isLoading['checkout'] ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              theme?.cart?.labelContinueShopping || 'Continue shopping'
            )}
          </Button>
          <Button
            type='button'
            variant='link'
            className='theme_button theme_cart_drawer_btn flex w-full items-center px-24'
            onClick={handleToCart}
            disabled={isLoading['checkout'] || pathname.startsWith('/checkout') || disableButton}
          >
            {isLoading['checkout'] ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <span className='mx-2'>{theme?.cart?.labelCheckout || 'Checkout'}</span>
            )}
          </Button>
        </div>
      </DrawerFooter>

      <ModalAlert
        title='Remove Active Promotion'
        description='This item currently has an active promotion with specific requirements. If you continue, the promotion will be removed. Are you sure you want to proceed?'
        openModal={modalConfirmSpecific.isShow}
        setOpenModal={(open) => {
          if (!open) {
            modalConfirmSpecific.data?.onCancel?.();
          }
          modalConfirmSpecific.setIsShow(open);
        }}
        onConfirm={modalConfirmSpecific.data?.onConfirm}
      />
    </>
  );
}
